import { Http } from '@mygpt/app/http';

/**
 * 获取帐号付费信息
 * @param params
 * @returns
 */
export const getPaymentInfo = () => {
  const { get } = Http.use();
  return get(`/stripes/my-plan`);
};

/**
 * 获取价格付费信息
 * @param params
 * @returns
 */
export const getPricingPlan = () => {
  const { get } = Http.use();
  return get(`/stripes/products`);
};

/**
 * 取消会员付费
 * @param params
 * @returns
 */
export const cancelPlan = () => {
  const { post } = Http.use();
  return post(`/stripes/cancel`);
};

// ------------------------------
// 新的套餐、订阅接口，上面的旧接口未来考虑废弃
// ------------------------------

/**
 * 套餐类型枚举
 * - TRIAL: 试用版本
 * - STARTER: 入门版本
 * - PRO: 专业版本
 * - BUSINESS: 商业版本
 * - BUSINESS_ELITE: 商业精英版本
 */
export enum PlanType {
  TRIAL = 'trial',
  STARTER = 'starter',
  PRO = 'pro',
  BUSINESS = 'business',
  BUSINESS_ELITE = 'business_elite',
}
/**
 * 套餐计划完整接口
 */
export interface Plan {
  /** 套餐ID */
  id: string;
  /** 创建时间 */
  created_at: string;
  /** 更新时间 */
  updated_at: string;
  /** 套餐名称 */
  name: string;
  /** 套餐类型 */
  plan_type: PlanType;
  /** 月费(单位: 日元)，null表示"需要询价" */
  price: number | null;
  /** 套餐说明或功能描述 */
  description: string | null;
  /** 存储容量限制(Byte)，null表示无上限 */
  storage_quota: number | null;
  /** Bot数量限制，null表示无上限 */
  bot_quota: number | null;
  /** 每月消息数限制，null表示无上限 */
  message_quota: number | null;
  message_quota_grant_type: string;
  /** Web网站导入页数限制，null表示无上限 */
  web_page_quota: number | null;
  /** 多模态解析文件数限制，null表示无上限 */
  multi_modal_parsing_quota: number | null;
  /** 是否启用Agent功能 */
  enable_agent: boolean;
  /** 是否启用多模态解析 */
  enable_multi_modal_parsing: boolean;
  /** 是否启用实时语音对话 */
  enable_voice_chat: boolean;
  /** 是否启用大屏操作UI */
  enable_large_screen_ui: boolean;
  /** 是否启用Canvas功能 */
  enable_canvas: boolean;
  /** 是否启用协同管理 */
  enable_collaboration: boolean;
  /** 是否启用Widget定制 */
  enable_widget_customization: boolean;
  /** 是否启用聊天历史分析 */
  enable_chat_history_analysis: boolean;
  /** 是否启用2D数字人 */
  enable_2d_digital_human: boolean;
  /** 可用的LLM模型列表 */
  available_llm_models: string[];
  /** 是否启用QA智能分类功能 */
  enable_question_tag: boolean;
}

export type EnableKey =
  | 'enable_agent'
  | 'enable_multi_modal_parsing'
  | 'enable_voice_chat'
  | 'enable_large_screen_ui'
  | 'enable_canvas'
  | 'enable_collaboration'
  | 'enable_widget_customization'
  | 'enable_chat_history_analysis'
  | 'enable_2d_digital_human'
  | 'enable_question_tag';

/**
 * 创建套餐计划的请求体
 * 注意：此接口不包含id和时间戳等自动生成的字段
 */
export interface PlanInput {
  /** 套餐名称（唯一） */
  name: string;
  /** 套餐类型 */
  // plan_type: PlanType;
  /** 月费(单位: 日元)，null表示"需要询价" */
  price?: number | null;
  /** 套餐说明或功能描述 */
  description?: string | null;
  /** 存储容量限制(Byte)，-1表示无上限 */
  storage_quota?: number | null;
  /** Bot数量限制，-1表示无上限 */
  bot_quota?: number | null;
  /** 每月消息数限制，-1表示无上限 */
  message_quota?: number;
  /** Web网站导入页数限制，-1表示无上限 */
  web_page_quota?: number | null;
  /** 多模态解析文件数限制，-1表示无上限 */
  multi_modal_parsing_quota?: number | null;
  /** 是否启用Agent功能 */
  enable_agent?: boolean;
  /** 是否启用多模态解析 */
  enable_multi_modal_parsing?: boolean;
  /** 是否启用实时语音对话 */
  enable_voice_chat?: boolean;
  /** 是否启用大屏操作UI */
  enable_large_screen_ui?: boolean;
  /** 是否启用Canvas功能 */
  enable_canvas?: boolean;
  /** 是否启用协同管理 */
  enable_collaboration?: boolean;
  /** 是否启用Widget定制 */
  enable_widget_customization?: boolean;
  /** 是否启用聊天历史分析 */
  enable_chat_history_analysis?: boolean;
  /** 是否启用2D数字人 */
  enable_2d_digital_human?: boolean;
  /** 可用的LLM模型列表 */
  available_llm_models?: string[];
}

/**
 * 分页响应接口
 * @template T 分页数据项类型
 */
export interface Page<T> {
  /** 当前页的数据项列表 */
  items: T[];
  /** 总数据项数量 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页大小 */
  size: number;
  /** 总页数 */
  pages: number;
}

/**
 * 获取所有套餐计划，支持分页
 * @param params 分页参数
 * @param params.page 页码，从1开始
 * @param params.size 每页记录数
 * @param params.order_by 排序字段，前缀'-'表示降序，如'-created_at'表示按创建时间降序
 * @returns 分页后的套餐计划列表
 */
export const getPlans = (params?: { page?: number; size?: number; order_by?: string }) => {
  const { get } = Http.use();
  return get<Page<Plan>>('/plans', { params });
};

/**
 * 创建套餐计划（仅超级管理员可用）
 * 注意：接口中不能修改/删除这个表的数组，特别是关键字段；因为可能影响已有的订阅
 * 如果要改，建议全新创建一个plan
 *
 * @param planData 套餐计划数据
 * @returns 创建的套餐计划
 */
export const createPlan = (planData: PlanInput) => {
  const { post } = Http.use();
  console.log(planData);
  return post<Plan>('/plans', planData);
};

// /**
//  * 获取当前用户的订阅信息
//  * @returns 当前用户的订阅信息
//  */
// export const getSubscriptionInfo = () => {
//   const { get } = Http.use();
//   return get(`/subscription`);
// };

/**
 * Add-on 目标字段枚举
 */
export enum AddOnTargetField {
  // 数值限制类字段
  MESSAGE_QUOTA = 'message_quota',
  // STORAGE_QUOTA = 'storage_quota',
  BOT_QUOTA = 'bot_quota',
  WEB_PAGE_QUOTA = 'web_page_quota',
  MULTI_MODAL_PARSING_QUOTA = 'multi_modal_parsing_quota',

  // 功能开关类字段（布尔型）
  // ENABLE_AGENT = 'enable_agent',
  // ENABLE_MULTI_MODAL_PARSING = 'enable_multi_modal_parsing',
  // ENABLE_VOICE_CHAT = 'enable_voice_chat',
  // ENABLE_LARGE_SCREEN_UI = 'enable_large_screen_ui',
  // ENABLE_CANVAS = 'enable_canvas',
  // ENABLE_COLLABORATION = 'enable_collaboration',
  // ENABLE_WIDGET_CUSTOMIZATION = 'enable_widget_customization',
  // ENABLE_CHAT_HISTORY_ANALYSIS = 'enable_chat_history_analysis',
  // ENABLE_2D_DIGITAL_HUMAN = 'enable_2d_digital_human',

  // 模型列表字段
  // AVAILABLE_LLM_MODELS = 'available_llm_models',
}
export interface AddOn {
  id: string;
  name: string;
  description: string;
  price: number;
  target_field: AddOnTargetField;
  value: number;
  duration_unit: string;
  duration_length: number;
  created_at: string;
  updated_at: string;
}

/**
 * 创建 Add-on 的请求体
 */
export interface AddOnInput {
  /** Add-on 名称 */
  name: string;
  /** Add-on 描述 */
  description?: string;
  /** 价格(单位: 日元)，null 表示需询价 */
  price?: number | null;
  /** 目标字段，必须是 AddOnTargetField 枚举中的有效值 */
  target_field: AddOnTargetField;
  /** 增值效果的具体值 */
  value: number | boolean | string[];
}

/**
 * 获取附加功能，支持分页
 * @param params 分页参数
 * @param params.page 页码，从1开始
 * @param params.size 每页记录数
 * @param params.order_by 排序字段，前缀'-'表示降序，如'-created_at'表示按创建时间降序
 * @returns 分页后的附加功能列表
 */
export const getAddOns = (params?: { page?: number; size?: number; order_by?: string }) => {
  const { get } = Http.use();
  return get<Page<AddOn>>('/add-ons', { params });
};

/**
 * 创建附加功能（仅超级管理员可用）
 * @param addOnData 附加功能数据
 * @returns 创建的附加功能
 */
export const createAddOn = (addOnData: AddOnInput) => {
  const { post } = Http.use();
  return post<AddOn>('/add-ons', addOnData);
};
